/**
 * Branch store for versioning system.
 *
 * See docs/docs/architecture/versioning.md for full implementation plan.
 *
 * Fields:
 * - activeBranchId: Currently selected branch ID (null = main, no header sent)
 * - branches: List of branches for current workspace
 * - setActiveBranch/fetchBranches/createBranch/mergeBranch: Actions
 *
 * Example usage:
 * ```ts
 * import { useBranchStore } from "@/hooks/use-branch-store";
 *
 * const { activeBranchId, branches, setActiveBranch, createBranch } = useBranchStore();
 *
 * // Switch to a different branch
 * setActiveBranch("branch_feature-x_abc1");
 *
 * // Switch back to main
 * setActiveBranch(null);
 *
 * // Create new branch
 * await createBranch("feature-x");
 * ```
 */

import { create } from "zustand";
import { persist } from "zustand/middleware";
import { API_CROWLINGO_AUTHED } from "@/api/core";

export interface Branch {
  id: string;
  name: string;
  is_default: boolean;
  created_at: string;
}

export interface MergeResult {
  applied: number;
  skipped: Array<{ logId: string; path: string; error: string }>;
  sourceDeleted: boolean;
}

interface BranchStore {
  /** Currently selected branch ID. null = main branch (no header sent, backend defaults to main) */
  activeBranchId: string | null;
  /** List of branches for the current workspace */
  branches: Branch[];
  /** Switch to a different branch */
  setActiveBranch: (branchId: string | null) => void;
  /** Fetch all branches for the current workspace */
  fetchBranches: () => Promise<void>;
  /** Create a new branch from current branch */
  createBranch: (name: string) => Promise<Branch>;
  /** Merge source branch into target branch */
  mergeBranch: (sourceBranchId: string, targetBranchId: string, deleteSource?: boolean) => Promise<MergeResult>;
  /** Delete a branch */
  deleteBranch: (branchId: string) => Promise<void>;
  /** Clear branches (e.g., on workspace change) */
  clearBranches: () => void;
}

export const useBranchStore = create<BranchStore>()(
  persist(
    (set) => ({
      activeBranchId: null, // null = main branch (header not sent)
      branches: [],

      setActiveBranch: (branchId) => {
        set({ activeBranchId: branchId });
      },

      fetchBranches: async () => {
        const response = await API_CROWLINGO_AUTHED.get<Branch[]>("/branches");
        set({ branches: response.data });
      },

      createBranch: async (name) => {
        const response = await API_CROWLINGO_AUTHED.post<Branch>("/branches", { name });
        const newBranch = response.data;
        set((state) => ({ branches: [...state.branches, newBranch] }));
        return newBranch;
      },

      mergeBranch: async (sourceBranchId, targetBranchId, deleteSource = false) => {
        const response = await API_CROWLINGO_AUTHED.post<MergeResult>(
          `/branches/${sourceBranchId}/merge`,
          { targetBranchId, deleteSource }
        );
        // If source was deleted, remove it from branches
        if (response.data.sourceDeleted) {
          set((state) => ({
            branches: state.branches.filter((b) => b.id !== sourceBranchId),
            // Reset to main if we were on the deleted branch
            activeBranchId: state.activeBranchId === sourceBranchId ? null : state.activeBranchId,
          }));
        }
        return response.data;
      },

      deleteBranch: async (branchId) => {
        await API_CROWLINGO_AUTHED.delete(`/branches/${branchId}`);
        set((state) => ({
          branches: state.branches.filter((b) => b.id !== branchId),
          // Reset to main if we were on the deleted branch
          activeBranchId: state.activeBranchId === branchId ? null : state.activeBranchId,
        }));
      },

      clearBranches: () => {
        set({ branches: [], activeBranchId: null });
      },
    }),
    {
      name: "branch-store",
      partialize: (state) => ({ activeBranchId: state.activeBranchId }),
    }
  )
);
